import { FC, memo, useCallback, useMemo } from 'react';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ServerTable } from 'shared/ui/ServerTable';
import { ReactComponent as BoxEditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Discount, useDeleteDiscountMutation, useGetPaginatedDiscountListingQuery, useUpdateDiscountMutation } from 'entities/Discount';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { ColumnsType } from 'antd/es/table';
import { Toggle } from 'shared/ui/Toggle';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { DiscountFormDrawer, discountFormDrawerActions, DrawerMode } from 'widgets/DiscountFormDrawer';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';

export const DiscountsTable: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const dispatch = useAppDispatch();

  const { openModal, closeModal } = useGlobalConfirmModal();

  const isForbiddenCreateDiscount = useCheckPermission(UserPermissions.CREATE_UNITS);
  const isForbiddenDeleteDiscount = useCheckPermission(UserPermissions.DELETE_UNITS);
  const isForbiddenEditDiscount = useCheckPermission(UserPermissions.EDIT_UNITS);

  const { data: warehouses } = useGetAllWarehousesQuery();
  const [deleteDiscount] = useDeleteDiscountMutation();
  const [updateDiscount] = useUpdateDiscountMutation();

  const createDiscount = useCallback((): void => {
    dispatch(discountFormDrawerActions.setOpenDiscountForm({ mode: DrawerMode.DISCOUNT_CREATE }));
  }, [dispatch]);

  const editDiscount = useCallback(
    (discount: Discount): void => {
      dispatch(discountFormDrawerActions.setOpenDiscountForm({ mode: DrawerMode.DISCOUNT_EDIT, discount }));
    },
    [dispatch],
  );

  const toggleDiscountActivation = useCallback(
    async (discount: Discount) => {
      try {
        await updateDiscount({ discountId: discount.discountId, isActive: !discount.isActive }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Discount status has been successfully changed'));
      } catch (error: CustomAny) {
        console.log(error);
        showNotification('error', t('Error', { ns: 'common' }), 'Error when changing discount status');
      }
    },
    [t, updateDiscount],
  );

  const handleDeleteDiscount = useCallback(
    async (selectedDiscount: Discount): Promise<void> => {
      try {
        await deleteDiscount(selectedDiscount.discountId).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Discount deleted successfully'));
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when deleting discount'));
        console.log('error', error);
      } finally {
        closeModal();
      }
    },
    [closeModal, deleteDiscount, t],
  );

  const openDiscountDeletionConfirmModal = useCallback(
    (selectedDiscount: Discount): void => {
      openModal({
        title: t('Delete discount'),
        description: t('Are you sure you want to delete this discount?'),
        onCancel: closeModal,
        onOk: async () => {
          await handleDeleteDiscount(selectedDiscount);
        },
        isConfirmDisabled: isForbiddenDeleteDiscount,
      });
    },
    [closeModal, handleDeleteDiscount, isForbiddenDeleteDiscount, openModal, t],
  );

  const rowActions: Array<ServerTableRowActions<Discount>> = useMemo(
    () => [
      {
        name: 'editDiscount',
        icon: <BoxEditIcon />,
        theme: 'clear',
        description: t('Edit discount'),
        onClick: editDiscount,
      },
      {
        name: 'deleteDiscount',
        icon: <DeleteIcon />,
        theme: 'clear',
        description: t('Delete discount'),
        onClick: openDiscountDeletionConfirmModal,
      },
    ],
    [editDiscount, openDiscountDeletionConfirmModal, t],
  );

  const tableActions: Array<ServerTableActions<Discount>> = useMemo(
    () => [
      {
        name: t('Add Discount'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: createDiscount,
        disabled: isForbiddenCreateDiscount,
      },
    ],
    [t, createDiscount, isForbiddenCreateDiscount],
  );

  const columns = useMemo<ColumnsType<Discount>>(
    () => [
      {
        title: t('Name'),
        key: 'name',
        render: (_, record) => {
          return <div className="text-primary font-normal">{record.name}</div>;
        },
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',
        render: (_, record) => {
          const assignedWarehouses = warehouses?.filter((warehouse) => record.warehouseIds?.includes(warehouse.warehouseId));

          if (!assignedWarehouses?.length) {
            return <div>{t('All')}</div>;
          }

          return (
            <div className="text-primary font-normal">
              {assignedWarehouses?.map((warehouse) => {
                return <div key={warehouse.warehouseId}>{warehouse.name}</div>;
              })}
            </div>
          );
        },
      },
      {
        title: t('Unit type'),
        key: 'unitType',
        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              <div>{t(record.unitType)}</div>
              <div className="text-sm font-normal">{record.unitName}</div>
            </div>
          );
        },
      },
      {
        title: t('Client loyalty'),
        key: 'loyaltyPeriod',
        render: (_, record) => {
          return <div className="text-primary font-normal">{t(record.loyaltyPeriod)}</div>;
        },
      },
      {
        title: t('Duration'),
        key: 'contractDuration',
        render: (_, record) => {
          return <div className="text-primary font-normal">{record.contractDurations?.join(',')}</div>;
        },
      },
      {
        title: t('Discount key'),
        key: 'discountKeys',
        render: (_, record) => {
          return <div className="text-primary">{record.discountKeys}</div>;
        },
      },
      {
        title: t('Options'),
        key: 'options',
        render: (_, record) => {
          return (
            <div>
              <div className="text-primary font-normal flex gap-1">
                {t('Account type')}: <div>{t(record.accountType)}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Period type')}: <div>{t(record.periodType)}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Payment frequency')}: <div>{t(record.paymentFrequency)}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Priority')}: <div>{record.priority}</div>
              </div>
            </div>
          );
        },
      },
      {
        title: t('Status'),
        key: 'isActive',
        render: (_, record) => (
          <div>
            <Toggle
              checked={record.isActive}
              isDisabled={isForbiddenEditDiscount}
              onChange={async () => {
                await toggleDiscountActivation(record);
              }}
            />
            <div className="text-xs text-primaryLight mt-2">{t(record.isActive ? 'Enabled' : 'Disabled')}</div>
          </div>
        ),
      },
      {
        title: t('Description'),
        key: 'description',
        width: '25%',
        render: (_, record) => {
          return <div className="text-primaryLight font-normal">{getLocalizedString(record.description)}</div>;
        },
      },
    ],
    [t, warehouses, isForbiddenEditDiscount, toggleDiscountActivation],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Discounts List')}</div>
      <ServerTable
        rowActions={rowActions}
        columns={columns}
        fetchData={useGetPaginatedDiscountListingQuery}
        rowKey="discountId"
        search
        tableActions={tableActions}
        tableActionsPosition="prev"
      />
      <DiscountFormDrawer />
    </div>
  );
});
